import React from "react";

/**
 * Contexto opt-in para que un componente de campo custom (`field.customComponent`)
 * acceda a datos que el contrato de props del control (value/onChange/...) no expone:
 *   - `recordId`: id del registro en edición (p.ej. consultar un campo que el server
 *     llena y no viaja en las props). undefined en create (aún no existe).
 *   - `values`: los valores VIVOS de los campos hermanos del form (reactivos al tipear),
 *     para lógica que depende de otro campo — p.ej. calcular un derivado según una
 *     ref hermana. Contiene los escalares/enum del form (clave = nombre del campo) y
 *     las refs simples como {displayId, id} | null. Files, subentidades, multi-refs
 *     y multilang no se incluyen.
 *
 * El form (create y update) lo provee SOLO cuando la entidad tiene algún campo con
 * `customComponent`. Los controles estándar lo ignoran; un customComponent que lo
 * necesite lo consume con `useFormRecord()`. El contrato de props del customComponent
 * queda intacto (no rompe componentes ya escritos).
 */
export interface FormRecordContextValue {
  /** Id del registro en edición. undefined en create (aún no existe). */
  recordId?: string;
  /** Valores vivos de los hermanos: escalares/enum por nombre; refs simples como {displayId, id} | null. */
  values?: Record<string, any>;
}

const FormRecordContext = React.createContext<FormRecordContextValue>({});

export const FormRecordProvider: React.FC<
  FormRecordContextValue & { children: React.ReactNode }
> = ({ recordId, values, children }) => (
  <FormRecordContext.Provider value={{ recordId, values }}>
    {children}
  </FormRecordContext.Provider>
);

export const useFormRecord = (): FormRecordContextValue =>
  React.useContext(FormRecordContext);
